import { TextFieldModule } from '@angular/cdk/text-field';
import { Component, Input, OnInit, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { StepperFormStore } from '../../../+state/firmware-package/stepper-form.store';

import { ReleaseNotesService } from './release-notes.service';

interface ReleaseNotesJson {
  notes: Record<string, string>;
}

export const invalidReleaseNotesErrorMessage = `
    Please add release notes for all supported languages in the described format:
    {
    "notes": {
      "en-US": "- Improved stability\\n- Fixed login issue\\n- Minor UI polish",
      "de-DE": "- Verbesserte Stabilität\\n- Anmeldefehler behoben\\n- Kleine UI Verbesserungen"
      }
    }
    `;

@Component({
  selector: 'remote-update-domain-release-notes',
  templateUrl: './release-notes.html',
  styleUrls: ['./release-notes.css'],
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, MatFormFieldModule, TextFieldModule, MatInputModule]
})
export class ReleaseNotes implements OnInit {
  @Input() currentSection = '';
  private readonly releaseNotesService = inject(ReleaseNotesService);
  private readonly store = inject(StepperFormStore);

  jsonExample = `
    {
      "notes": {
        "en-US": "- Improved stability\\n- Fixed login issue",
        "de-DE": "- Verbesserte Stabilität\\n- Anmeldefehler behoben"
      }
    }
  `;

  releaseNotesForm = new FormGroup({
    notes: new FormControl<string>('', Validators.required)
  });

  formattedJsonString = '';
  requiredLanguageCodes: string[] = [];

  get error(): string | undefined {
    return this.store.errors().releaseNotes;
  }

  async ngOnInit(): Promise<void> {
    this.requiredLanguageCodes = await this.releaseNotesService
      .getSupportedLanguages()
      .then(languages => languages?.map(language => language.languageCode) ?? []);

    const existingReleaseNotes = this.store.releaseNotes().release_notes;
    if (existingReleaseNotes) {
      this.formattedJsonString = existingReleaseNotes;
      this.releaseNotesForm.get('notes')?.setValue(existingReleaseNotes, { emitEvent: false });
      this.store.setReleaseNotesValue(
        {
          release_notes: this.formattedJsonString,
          isValid: true
        },
        ''
      );
    }

    this.releaseNotesForm.get('notes')?.valueChanges.subscribe(value => {
      this.processInput(value ?? '');
    });
  }

  processInput(input: string): void {
    this.formattedJsonString = '';

    const trimmedInput = input.trim();
    if (!trimmedInput) {
      this.setInvalidReleaseNotes(
        'Release notes can not be empty. Please provide all release notes'
      );
      return;
    }

    const notesBlockMatch = trimmedInput.match(/"notes"\s*:\s*{([\s\S]*)}/);
    if (notesBlockMatch) {
      const keys = [...notesBlockMatch[1].matchAll(/"([a-z]{2}(?:-[A-Z]{2})?)"\s*:/g)].map(
        m => m[1]
      );
      const duplicates = keys.filter((key, index) => keys.indexOf(key) !== index);
      if (duplicates.length > 0) {
        this.setInvalidReleaseNotes(`Duplicate language codes found: ${duplicates.join(', ')}`);
        return;
      }
    }

    let parsed: ReleaseNotesJson;

    try {
      parsed = JSON.parse(trimmedInput);
    } catch {
      this.setInvalidReleaseNotes(invalidReleaseNotesErrorMessage);
      return;
    }

    if (!this.isValidReleaseNotesJson(parsed)) {
      const missing = this.requiredLanguageCodes.filter(code => !(code in parsed.notes));
      const extra = Object.keys(parsed.notes).filter(
        code => !this.requiredLanguageCodes.includes(code)
      );
      const empty = Object.entries(parsed.notes)
        .filter(([, text]) => typeof text !== 'string' || text.trim() === '')
        .map(([code]) => code);

      const messages: string[] = [];

      if (missing.length) messages.push(`Missing languages: ${missing.join(', ')}`);
      if (extra.length) messages.push(`Unsupported languages: ${extra.join(', ')}`);
      if (empty.length) messages.push(`Languages with empty text: ${empty.join(', ')}`);

      const message =
        messages.length > 0
          ? messages.join(' | ')
          : 'Release notes object is missing required language entries or contains empty texts.';

      this.setInvalidReleaseNotes(message);
      return;
    }

    const notes = parsed.notes;

    this.formattedJsonString = JSON.stringify({ notes }, null, 2);

    this.store.setReleaseNotesValue(
      {
        release_notes: this.formattedJsonString,
        isValid: true
      },
      ''
    );
  }

  private isValidReleaseNotesJson(data: ReleaseNotesJson): boolean {
    if (!data || typeof data !== 'object') return false;
    if (!data.notes || typeof data.notes !== 'object') return false;

    const notes = data.notes;

    for (const code of this.requiredLanguageCodes) {
      const text = notes[code];
      if (typeof text !== 'string' || text.trim() === '') {
        return false;
      }
    }

    const keys = Object.keys(notes);
    if (keys.length !== this.requiredLanguageCodes.length) return false;

    return this.requiredLanguageCodes.every(code => code in notes);
  }

  private setInvalidReleaseNotes(errorMessage: string): void {
    this.store.setReleaseNotesValue(
      {
        release_notes: '',
        isValid: false
      },
      errorMessage
    );
    const control = this.releaseNotesForm.get('notes');
    control?.setErrors({ invalidReleaseNotes: true });
    control?.markAsTouched();
    control?.markAsDirty();
  }
}
